<!--赖传峰-->
<!--2021/12/22-->
<template>
  <div style="flex: auto; min-width: 0">
    <nav
      ref="folderNavigator"
      :class="props.viewType"
      class="folder-navigator"
    >
      <ul @click="crumbsClickTrack">
        <li>
          <NuxtLink
            :class="folders.length ? undefined : 'active'"
            :to="`/s/${shareId}`"
            class="pp-link-button"
          >
            {{ $t('all-files') }}
          </NuxtLink>
        </li>
        <li v-if="ellipseIdx.length">
          <RightIcon :size="18" />
          <ElPopover
            :offset="4"
            :show-arrow="false"
            placement="bottom-start"
            popper-class="folder-navigator__popover"
            trigger="click"
          >
            <template #reference>
              <a
                class="pp-link-button"
                style="color: var(--color-secondary-text);"
              >
                <EllipsisIcon />
              </a>
            </template>
            <ol>
              <li
                v-for="item in folders.filter((_, idx) => ellipseIdx.includes(idx + 1))"
                :key="item.id"
              >
                <NuxtLink
                  :to="computeRouteConfig(item.id)"
                  class="pp-link-button"
                >
                  <FolderIcon />
                  <span class="ellipsis">{{ item.name }}</span>
                </NuxtLink>
              </li>
            </ol>
          </ElPopover>
        </li>
        <li
          v-for="(item, idx) in folders4Render"
          :key="item.id"
          style="min-width: 0"
        >
          <RightIcon :size="18" />
          <a
            v-if="idx === folders4Render.length - 1"
            class="pp-link-button ellipsis active"
          >
            {{ item.name }}
          </a>
          <NuxtLink
            v-else
            :to="computeRouteConfig(item.id)"
            class="pp-link-button ellipsis"
          >
            {{ item.name }}
          </NuxtLink>
        </li>
      </ul>
      <!--作为一个参照物存在，代表原始渲染出来的节点，计算的时候，需要获取节点的宽度-->
      <ul
        ref="shadowBreadcrumb"
        class="shadow"
      >
        <li><a class="pp-link-button">{{ $t('all-files') }}</a></li>
        <li
          v-for="(item) in folders"
          :key="item.id"
        >
          <DownIcon :size="18" />
          <a class="pp-link-button ellipsis">
            {{ item.name }}
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>
<script
  lang="ts"
  setup
>
import {
  computed,
  nextTick,
  onMounted,
  ref,
  useNuxtApp,
  useRoute,
  watch,
} from '#imports'
import { getBatchFileInfo } from '@/api'
import DownIcon from '@/components/icons/DownIcon.vue'
import FolderIcon from '@/components/icons/FolderIcon.vue'
import {
  shareTrackParams,

} from '@/components/share/share.script'
import useShareId from '@/composables/useShareId'
import $track from '@/track/webShare'
import {
  StorageKey,
  ViewType,
} from '@/types/enum'
import { useElementSize } from '@vueuse/core'
import type { RouteLocationRaw } from 'vue-router'
import EllipsisIcon from '~/components/icons/EllipsisIcon.vue'
import RightIcon from '~/components/icons/RightIcon.vue'

interface FolderData {
  name: string
  id: string
}

const props = defineProps<{ viewType: ViewType }>()

const { $getPasscodeToken } = useNuxtApp()

const folderNavigator = ref<HTMLElement>()
const shadowBreadcrumb = ref<HTMLElement>()
const folders = ref<FolderData[]>([])
const ellipseIdx = ref<number[]>([]) // 需要省略显示的元素

const shareId = useShareId()
const route = useRoute()
const parentIds = computed(() => route.params.parent_ids)
const { width } = useElementSize(shadowBreadcrumb)
const folders4Render = computed(() => folders.value.filter((_, idx) => !ellipseIdx.value.includes(idx + 1)))
const computeRouteConfig = (id: string): RouteLocationRaw => ({
  params: {
    share_id: shareId.value,
    parent_ids: parentIds.value.slice(0, parentIds.value.indexOf(id) + 1),
  },
})

const resizeBreadcrumb = () => {
  ellipseIdx.value = []
  const dom = shadowBreadcrumb.value
  if (!dom) return

  const ellipseItemWith = 66 // 如果以 "> ..." 的形式显示的宽度
  const maximWidth = width.value

  // 最大长度无法获取
  if (!maximWidth) return

  const itemWidths = Array.from(dom.children).map(elt => elt.getBoundingClientRect().width)

  /* 数据是否已经全部渲染成了元素节点 */
  if (((itemWidths.length - 1) < folders.value.length) || !folders.value.length) return

  const totalWidth = itemWidths.reduce((prev, curr) => prev + curr, 0)

  if (totalWidth <= maximWidth) return

  let idx = 1
  let targetSize = totalWidth
  const ellipseIdxSet = new Set<number>()
  while ((idx < folders.value.length) && (targetSize > maximWidth)) { // 若有一个条件达到，即停止循环
    targetSize = totalWidth - itemWidths[idx] + ellipseItemWith // 总长度依次递减
    ellipseIdxSet.add(idx)
    idx += 1
  }
  ellipseIdx.value = [...ellipseIdxSet]
}

const foldersSession = {
  get() {
    const data = sessionStorage.getItem(StorageKey.NAV_FOLDERS)
    if (!data) return []
    try {
      const parsedData = JSON.parse(data)
      if (Array.isArray(parsedData)) return parsedData
      return []
    } catch (e) {
      console.error(e)
      return []
    }
  },
  set(data: FolderData[]) {
    try {
      const oldData = foldersSession.get()
      const newData = (data || []).concat(oldData)
        .filter((el, idx, arr) => arr.findIndex(_el => _el.id === el.id) === idx)
      sessionStorage.setItem(StorageKey.NAV_FOLDERS, JSON.stringify(newData))
      return true
    } catch (e) {
      console.error(e)
      return false
    }
  },
}

const initial = async () => {
  if (import.meta.server) return
  const cached = foldersSession.get()
  if (
    Array.isArray(parentIds.value)
    && parentIds.value.every(parentId => cached.find(folder => folder.id === parentId))
  ) {
    folders.value = cached.filter(folder => parentIds.value.includes(folder.id))
  } else {
    const data = Array.isArray(parentIds.value) && (await getBatchFileInfo({
      share_id: shareId.value,
      file_ids: parentIds.value,
      pass_code_token: $getPasscodeToken().value,
    })).data.value?.files.map(el => ({
      id: el.id,
      name: el.name,
    }))

    data && foldersSession.set(data)
    folders.value = data || []
  }

  await nextTick()
  resizeBreadcrumb()
}

onMounted(initial)

watch(parentIds, initial)
watch(width, resizeBreadcrumb)

const crumbsClickTrack = () => {
  $track.web_share_page_click({ ...shareTrackParams(), button: 'crumbs' })
}

</script>

<style
  lang="less"
  scoped
>
@import '@/assets/styles/util.less';

.folder-navigator {
  width: 100%;
  height: 36px;
  overflow: hidden;
}

.shadow {
  visibility: hidden;
  opacity: 0;
}

ul {
  display: flex;

  li {
    font-size: 13px;
    display: flex;
    white-space: nowrap;
    align-items: center;
    color: var(--color-secondary-text);

    .pp-link-button {
      transition: all var(--transition-delay) var(--transition-animation);
      border-radius: 4px;
      padding: 0 8px;

      &:hover {
        background: var(--color-active);
      }

      &.active {
        &:hover {
          background: none;
        }

        cursor: default;
        color: #000 !important;
      }
    }
  }

  li:not(:first-child) {
    .pp-link-button {
      margin-left: 8px;
    }
  }
}

.ellipsis {
  #ellipsis();

  &.pp-link-button {
    line-height: 36px;
    display: unset;
  }
}

</style>
<style lang="less">
.folder-navigator__popover {
  max-width: 640px;
  min-width: 200px !important;
  width: auto !important;
  --el-popover-padding: 8px;

  .pp-link-button {
    justify-content: left;
    line-height: 36px;
    padding: 0 8px;
    border-radius: 4px;

    &:hover {
      background: var(--color-active);
    }

    .pp-icon {
      margin-right: 15px;
    }
  }

  li {
    padding: 0 8px;
  }
}

@media screen and (max-width: 640px) {
  .folder-navigator__popover {
    max-width: 100%;
  }
}
</style>
